/**
 * editorTransform — SvgFigureEditor 의 변환 수학.
 *
 * 행렬은 `{a,b,c,d,e,f}` 순수 객체로만 다룬다 — DOMMatrix/DOMPoint 는 jsdom 에 없고, 브라우저의 `getScreenCTM()` 도
 * 결국 a~f 여섯 수라 테스트에서 mock 하기 쉽다. transform 속성은 **문자열 앞에 덧붙이는 방식**(시제품 editor.html)으로
 * 합성한다 — 기존 transform 은 글자 그대로 보존되고, 결과는 `matrix(…)` 가 아니라 `translate/rotate/scale` 로 남아
 * 모델이 SVG 원문을 읽을 때(변형·AI 요청) 뜻이 보인다. 같은 종류가 연속되면 하나로 합친다(방향키 연타가 길어지지 않게).
 */

export const IDENTITY = Object.freeze({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

/** SVGMatrix/DOMMatrix → 순수 객체(없으면 null). */
export function plain(m) {
  if (!m) return null;
  const out = { a: m.a, b: m.b, c: m.c, d: m.d, e: m.e, f: m.f };
  return Object.values(out).every(Number.isFinite) ? out : null;
}

/** m × n (n 을 먼저 적용). */
export function multiply(m, n) {
  return {
    a: m.a * n.a + m.c * n.b,
    b: m.b * n.a + m.d * n.b,
    c: m.a * n.c + m.c * n.d,
    d: m.b * n.c + m.d * n.d,
    e: m.a * n.e + m.c * n.f + m.e,
    f: m.b * n.e + m.d * n.f + m.f,
  };
}

/** 역행렬(특이 행렬이면 null). */
export function invert(m) {
  const det = m.a * m.d - m.b * m.c;
  if (!det || !Number.isFinite(det)) return null;
  return {
    a: m.d / det,
    b: -m.b / det,
    c: -m.c / det,
    d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det,
    f: (m.b * m.e - m.a * m.f) / det,
  };
}

export function apply(m, x, y) {
  return { x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f };
}

/** 속성에 쓰는 수 — 소수 3자리, 뒤 0 제거, -0 은 0. */
export function fmt(n) {
  const v = Number(Number(n).toFixed(3));
  return String(v === 0 ? 0 : v);
}

const round3 = (n) => Number(Number(n).toFixed(3));
const join = (head, rest) => [head, rest].filter(Boolean).join(' ');
const NUM = '[-+]?(?:\\d+\\.?\\d*|\\.\\d+)(?:[eE][-+]?\\d+)?';
const HEAD_TRANSLATE = new RegExp(`^translate\\(\\s*(${NUM})(?:[\\s,]+(${NUM}))?\\s*\\)\\s*,?\\s*([\\s\\S]*)$`);
const HEAD_ROTATE = new RegExp(`^rotate\\(\\s*(${NUM})[\\s,]+(${NUM})[\\s,]+(${NUM})\\s*\\)\\s*,?\\s*([\\s\\S]*)$`);

/** 각도를 (-180, 180] 로. */
export function normAngle(deg) {
  const a = round3(((((deg + 180) % 360) + 360) % 360) - 180);
  return a === -180 ? 180 : a;
}

/** 기존 transform 앞에 translate 를 합성. 앞이 이미 translate 면 더한다. 결과가 항등이면 ''. */
export function composeTranslate(existing, dx, dy) {
  const rest = (existing || '').trim();
  const x = round3(dx);
  const y = round3(dy);
  if (!x && !y) return rest;
  const m = HEAD_TRANSLATE.exec(rest);
  if (m) {
    const tx = round3(Number(m[1]) + x);
    const ty = round3(Number(m[2] || 0) + y);
    return join(tx || ty ? `translate(${fmt(tx)} ${fmt(ty)})` : '', m[3]);
  }
  return join(`translate(${fmt(x)} ${fmt(y)})`, rest);
}

/** 기존 transform 앞에 (cx, cy) 중심 회전을 합성. 앞이 같은 중심의 rotate 면 각을 더한다. */
export function composeRotate(existing, deg, cx, cy) {
  const rest = (existing || '').trim();
  const a = normAngle(deg);
  if (!a) return rest;
  const m = HEAD_ROTATE.exec(rest);
  if (m && Math.abs(Number(m[2]) - cx) < 0.01 && Math.abs(Number(m[3]) - cy) < 0.01) {
    const sum = normAngle(Number(m[1]) + a);
    return join(sum ? `rotate(${fmt(sum)} ${m[2]} ${m[3]})` : '', m[4]);
  }
  return join(`rotate(${fmt(a)} ${fmt(cx)} ${fmt(cy)})`, rest);
}

/** 기존 transform 앞에 (cx, cy) 기준 scale 을 합성 — translate(c) scale(s) translate(-c). 1 배면 그대로. */
export function composeScale(existing, sx, sy, cx, cy) {
  const rest = (existing || '').trim();
  const x = fmt(sx);
  const y = fmt(sy);
  if (x === '1' && y === '1') return rest;
  const s = x === y ? x : `${x} ${y}`;
  if (!round3(cx) && !round3(cy)) return join(`scale(${s})`, rest);
  return join(`translate(${fmt(cx)} ${fmt(cy)}) scale(${s}) translate(${fmt(-cx)} ${fmt(-cy)})`, rest);
}

export const snap = (v, grid) => (grid > 0 ? Math.round(v / grid) * grid : v);
export const snapPoint = (p, grid) => (p && grid > 0 ? { x: snap(p.x, grid), y: snap(p.y, grid) } : p);

// ── DOM 읽기(getScreenCTM/getBBox — jsdom 엔 없으므로 없으면 null) ──

export function screenMatrix(el) {
  if (!el || typeof el.getScreenCTM !== 'function') return null;
  try {
    return plain(el.getScreenCTM());
  } catch (e) {
    return null;
  }
}

/** client 좌표 → el 의 사용자 좌표(el 자신의 CTM 역행렬). 못 구하면 null. */
export function toUserPoint(el, clientX, clientY) {
  const m = screenMatrix(el);
  const inv = m && invert(m);
  return inv ? apply(inv, clientX, clientY) : null;
}

export function bbox(el) {
  if (!el || typeof el.getBBox !== 'function') return null;
  try {
    const b = el.getBBox();
    return b && [b.x, b.y, b.width, b.height].every(Number.isFinite) ? b : null;
  } catch (e) {
    return null;
  }
}

/** 요소들의 화면(client) 경계 상자 — getBBox 네 모서리를 getScreenCTM 으로 옮겨 합친다. 하나도 못 구하면 null. */
export function screenBox(els) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of els) {
    const b = bbox(el);
    const m = screenMatrix(el);
    if (!b || !m) continue;
    for (const [x, y] of [[b.x, b.y], [b.x + b.width, b.y], [b.x, b.y + b.height], [b.x + b.width, b.y + b.height]]) {
      const p = apply(m, x, y);
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }
  return Number.isFinite(minX) ? { x: minX, y: minY, width: maxX - minX, height: maxY - minY } : null;
}
